import React, { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTranslation } from "react-i18next";
import apiClient from "../utils/apiClient";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { ActPrintType, ActViewer } from "../components/ActViewer";
import ActAction from "../enum/ActAction.enum";
import type { Act } from "../types/act";
import ActStatus from "../enum/ActStatus.enum";
import StyledDrawer from "../components/StyledDrawer";
import AgentCpaClients from "../components/AgentCpaClients";

type ApiClientItem = {
  initials: string;
};

type CpaApiResponse = {
  success: boolean;
  startMonthAndYear: string;
  monthlyCount: number;
  yearProgressToAct: number;
  lifetimeCount: number;
  hasApprovedAct: boolean;
  clients: ApiClientItem[] | string;
};

type CpaRow = {
  id: number;
  fullname: string;
};

type AdminRow = {
  id: number;
  agent_tn_id: string;
  fullname: string;
  monthlyCount: number;
  yearProgressToAct: number;
  hasAct: boolean;
};

type Period = { month: string; year: string };

const DetailingCpaTab = () => {
  const { t } = useTranslation();

  const [month, setMonth] = useState<string>(dayjs().format("MM"));
  const [year, setYear] = useState<string>(dayjs().format("YYYY"));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");

  const [hasData, setHasData] = useState<boolean>(false);

  const [lifetimeCount, setLifetimeCount] = useState<number>(0);
  const [yearProgressToAct, setYearProgressToAct] = useState<number>(0);
  const [monthlyCount, setMonthlyCount] = useState<number>(0);
  const [startMonthAndYear, setStartMonthAndYear] = useState<string>("");
  const [hasApprovedAct, setHasApprovedAct] = useState<boolean>(false);
  const [rows, setRows] = useState<CpaRow[]>([]);

  const [adminRows, setAdminRows] = useState<AdminRow[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<{
    tnId: string;
    fullname: string;
  } | null>(null);

  const { isAdmin, user } = useSelector((s: RootState) => s.user);

  const [isLoadingActs, setIsLoadingActs] = useState(false);
  const [actsByTnId, setActsByTnId] = useState<Record<string, Act>>({});
  const [selectedAct, setSelectedAct] = useState<Act | undefined>();
  const [actViewType, setActViewType] = useState<ActPrintType>(
    ActPrintType.ACT,
  );

  const [reportPeriod, setReportPeriod] = useState<Period>({
    month: dayjs().format("MM"),
    year: dayjs().format("YYYY"),
  });

  const loadActs = useCallback(
    async (p?: Period) => {
      const mm = p?.month ?? reportPeriod.month;
      const yy = p?.year ?? reportPeriod.year;

      setIsLoadingActs(true);
      try {
        const resp = await apiClient.get(`/acts?month=${mm}&year=${yy}`);
        if (!resp.data?.success) return { map: {} as Record<string, Act> };

        const map: Record<string, Act> = {};
        (resp.data.rows ?? []).forEach((a: Act) => {
          if (a?.tn_id) map[String(a.tn_id)] = a;
        });

        setActsByTnId(map);

        setSelectedAct((prev) => {
          if (!prev?.tn_id) return prev;
          return map[String(prev.tn_id)] ?? prev;
        });

        return { map };
      } catch (e) {
        console.error("Ошибка запроса актов:", e);
        return { map: {} as Record<string, Act> };
      } finally {
        setIsLoadingActs(false);
      }
    },
    [reportPeriod.month, reportPeriod.year],
  );

  const openActForTnId = useCallback(
    async (tnId: string, type: ActPrintType = ActPrintType.ACT, p?: Period) => {
      if (!tnId) return;

      let act = actsByTnId?.[tnId];

      if (!act) {
        const { map } = await loadActs(p);
        act = map?.[tnId];
      }

      if (!act) {
        console.warn("Акт не найден для tn_id:", tnId);
        return;
      }

      setActViewType(type);
      setSelectedAct(act);
    },
    [actsByTnId, loadActs],
  );

  const years = useMemo(
    () => Array.from(Array(5).keys()).map((i) => +dayjs().format("YYYY") - i),
    [],
  );

  const columns = useMemo<GridColDef<CpaRow>[]>(
    () => [
      {
        field: "fullname",
        headerName: t("detailingCpa.columns.fio"),
        flex: 1,
        minWidth: 260,
      },
    ],
    [t],
  );

  const adminColumns = useMemo<GridColDef<AdminRow>[]>(
    () => [
      {
        field: "fullname",
        headerName: t("detailingCpa.columns.agentFio"),
        flex: 2,
        minWidth: 260,
        renderCell: (params) => (
          <Box
            sx={{ cursor: "pointer", color: "primary.main", width: "100%" }}
            onClick={() =>
              setSelectedAgent({
                tnId: String(params.row.agent_tn_id ?? ""),
                fullname: String(params.row.fullname ?? ""),
              })
            }
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: "monthlyCount",
        headerName: t("detailingCpa.columns.monthlyCount"),
        flex: 1,
        type: "number",
        minWidth: 160,
      },
      {
        field: "yearProgressToAct",
        headerName: t("detailingCpa.columns.yearProgressToAct"),
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const value = Number(params.row.yearProgressToAct ?? 0);
          return `${value} / 20`;
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 140,
        sortable: false,
        renderCell: (params) => {
          const tnId = String(params.row.agent_tn_id ?? "");
          if (!tnId) return null;

          const act = actsByTnId?.[tnId];
          if (!params.row.hasAct && !act) return null;

          const approved = act?.status === ActStatus.APPROVED;

          return (
            <Button
              size="small"
              variant="outlined"
              color={approved ? "success" : "primary"}
              startIcon={<FileDownloadIcon />}
              disabled={!act || isLoadingActs}
              onClick={(e) => {
                e.stopPropagation();
                setActViewType(ActPrintType.ACT);
                setSelectedAct(act);
              }}
            >
              {t("detailingCpa.act")}
            </Button>
          );
        },
      },
    ],
    [actsByTnId, isLoadingActs, t],
  );

  const parseClients = (
    clients: CpaApiResponse["clients"],
  ): ApiClientItem[] => {
    if (!clients) return [];
    if (Array.isArray(clients)) return clients;
    if (typeof clients === "string") {
      try {
        const parsed = JSON.parse(clients);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const handleGetReport = async () => {
    setIsLoading(true);
    setErrorText("");

    const period = { month, year };
    setReportPeriod(period);

    try {
      const url = isAdmin
        ? `/detailing-cpa-admin?month=${period.month}&year=${period.year}`
        : `/detailing-cpa?month=${period.month}&year=${period.year}`;

      const resp = await apiClient.get(url);

      if (!resp?.data?.success) {
        setErrorText(t("detailingCpa.errors.getDetailing"));
        setHasData(false);
        setRows([]);
        setAdminRows([]);
        return;
      }

      await loadActs(period);

      if (isAdmin) {
        const raw = Array.isArray(resp.data.rows) ? resp.data.rows : [];

        const mapped: AdminRow[] = raw.map((r: any, idx: number) => ({
          id: Number(r.id ?? idx + 1),
          agent_tn_id: String(r.agent_tn_id ?? ""),
          fullname: String(r.fullname ?? ""),
          monthlyCount: Number(r.monthlyCount ?? 0),
          yearProgressToAct: Number(r.yearProgressToAct ?? 0),
          hasAct: Boolean(r.hasAct ?? false),
        }));

        setAdminRows(mapped);
        setHasData(true);

        setRows([]);
        setStartMonthAndYear("");
        setMonthlyCount(0);
        setYearProgressToAct(0);
        setLifetimeCount(0);
        setHasApprovedAct(false);

        return;
      }

      const data = resp.data as CpaApiResponse;

      setStartMonthAndYear(String(data.startMonthAndYear ?? ""));
      setMonthlyCount(Number(data.monthlyCount ?? 0));
      setYearProgressToAct(Number(data.yearProgressToAct ?? 0));
      setLifetimeCount(Number(data.lifetimeCount ?? 0));
      setHasApprovedAct(Boolean(data.hasApprovedAct ?? false));

      const clientItems = parseClients(data.clients).filter(
        (c) => String(c?.initials ?? "").trim().length > 0,
      );

      const mapped: CpaRow[] = clientItems.map((c, idx) => ({
        id: idx + 1,
        fullname: String(c.initials ?? ""),
      }));

      setRows(mapped);
      setAdminRows([]);
      setHasData(true);
    } catch (e) {
      console.error("CPA request error:", e);
      setErrorText(t("detailingCpa.errors.request"));
      setHasData(false);
      setRows([]);
      setAdminRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const myTnId = String(user?.user_id_tn ?? "");
  const myAct = actsByTnId?.[myTnId];
  const myActApproved = myAct?.status === ActStatus.APPROVED;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <FormControl sx={{ minWidth: 150, mr: 1 }} size="small">
          <InputLabel id="cpa-select-month-label">
            {t("detailingCpa.month")}
          </InputLabel>
          <Select
            labelId="cpa-select-month-label"
            id="cpa-select-month"
            value={month}
            label={t("detailingCpa.month")}
            size="small"
            onChange={(e) => setMonth(String(e.target.value))}
            variant="outlined"
          >
            <MenuItem value="01">{t("detailingCpa.months.january")}</MenuItem>
            <MenuItem value="02">{t("detailingCpa.months.february")}</MenuItem>
            <MenuItem value="03">{t("detailingCpa.months.march")}</MenuItem>
            <MenuItem value="04">{t("detailingCpa.months.april")}</MenuItem>
            <MenuItem value="05">{t("detailingCpa.months.may")}</MenuItem>
            <MenuItem value="06">{t("detailingCpa.months.june")}</MenuItem>
            <MenuItem value="07">{t("detailingCpa.months.july")}</MenuItem>
            <MenuItem value="08">{t("detailingCpa.months.august")}</MenuItem>
            <MenuItem value="09">{t("detailingCpa.months.september")}</MenuItem>
            <MenuItem value="10">{t("detailingCpa.months.october")}</MenuItem>
            <MenuItem value="11">{t("detailingCpa.months.november")}</MenuItem>
            <MenuItem value="12">{t("detailingCpa.months.december")}</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150, mr: 1 }} size="small">
          <InputLabel id="cpa-select-year-label">
            {t("detailingCpa.year")}
          </InputLabel>
          <Select
            labelId="cpa-select-year-label"
            id="cpa-select-year"
            value={year}
            label={t("detailingCpa.year")}
            size="small"
            onChange={(e) => setYear(String(e.target.value))}
            variant="outlined"
          >
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleGetReport}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {t("detailingCpa.get")}
        </Button>

        {!isAdmin && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<FileDownloadIcon />}
            disabled={!hasData || !myActApproved || isLoading || isLoadingActs}
            onClick={() => {
              setActViewType(ActPrintType.ACT);
              setSelectedAct(myAct);
            }}
          >
            {t("detailingCpa.act")}
          </Button>
        )}
      </Stack>

      {errorText && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {errorText}
        </Typography>
      )}

      {hasData && (
        <>
          {!isAdmin && (
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              sx={{
                p: 1,
                borderLeft: "3px solid #72BF44",
                backgroundColor: "#FFFDE7",
                mb: 1,
              }}
            >
              <Typography variant="body2">
                <b>{t("detailingCpa.total")}:</b>{" "}
                <strong>{lifetimeCount}</strong>
              </Typography>

              <Typography variant="body2">
                <b>{t("detailingCpa.monthly")}:</b>{" "}
                <strong>{monthlyCount}</strong>
              </Typography>

              <Typography variant="body2">
                <b>{t("detailingCpa.toAct")}:</b>{" "}
                <strong>{yearProgressToAct}/20</strong>
              </Typography>
            </Stack>
          )}

          <Box style={{ display: "flex", flexDirection: "column" }}>
            {isAdmin ? (
              <DataGrid<AdminRow>
                rows={adminRows}
                columns={adminColumns}
                loading={isLoading}
                pagination
                onRowClick={(params) => {
                  setSelectedAgent({
                    tnId: String(params.row.agent_tn_id ?? ""),
                    fullname: String(params.row.fullname ?? ""),
                  });
                }}
                sx={{
                  "& .MuiDataGrid-row": { cursor: "pointer" },
                  "--DataGrid-rowBorderColor": "#D1D3D4",
                  "& .MuiDataGrid-columnHeaderTitleContainer": {
                    fontSize: 12,
                    lineHeight: "110%",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: 16,
                    borderTop: "0.5px solid #D1D3D4",
                  },
                  fontFamily: "inherit",
                  borderWidth: 0,
                  background: "#ffffff",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
              />
            ) : (
              <DataGrid<CpaRow>
                rows={rows}
                columns={columns}
                loading={isLoading}
                pagination
                sx={{
                  "--DataGrid-rowBorderColor": "#D1D3D4",
                  "& .MuiDataGrid-columnHeaderTitleContainer": {
                    fontSize: 12,
                    lineHeight: "110%",
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: 16,
                    borderTop: "0.5px solid #D1D3D4",
                  },
                  fontFamily: "inherit",
                  borderWidth: 0,
                  background: "#ffffff",
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
              />
            )}
          </Box>

          <StyledDrawer
            show={Boolean(selectedAgent)}
            width="60vw"
            title={t("detailingCpa.agentDrawerTitle", {
              name: selectedAgent?.fullname,
              id: selectedAgent?.tnId,
            })}
            onClose={() => setSelectedAgent(null)}
            ContentComponent={AgentCpaClients}
            contentProps={{
              agentTnId: selectedAgent?.tnId ?? "",
              month,
              year,
            }}
          />

          {selectedAct && selectedAct?.id > 0 && (
            <ActViewer
              act={selectedAct}
              onClose={() => setSelectedAct(undefined)}
              onAction={(action: ActAction) => {
                loadActs({ month, year });
                handleGetReport();
              }}
              type={actViewType}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default DetailingCpaTab;
